'use client';

import { Star } from 'lucide-react';
import { motion, useInView, Variants } from 'motion/react';
import { CSSProperties, useRef } from 'react';

import { DEFAULT_EASE } from '@natu/utils/consts';

import { calculatePercentage } from './utils';

export interface RatingProps {
  count?: number;
  value?: string;
  className?: string;
}

const containerVariants: Variants = {
  glow: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  glow: {
    scale: [1, 1.25, 1],
    transition: {
      ease: DEFAULT_EASE,
    },
  },
};

const AnimatedStar = motion.create(Star);

export const Rating = ({ count = 5, value = '5', className }: RatingProps) => {
  const stars = Array.from({ length: count }, (_, i) => i);
  const starPercentageWidth = calculatePercentage(value, count);

  const ref = useRef(null);

  const isInView = useInView(ref, { once: true });

  const topStarStyles = {
    '--star-width': starPercentageWidth,
  } as CSSProperties;

  return (
    <div className={className} ref={ref}>
      <div className="relative flex max-w-max items-center gap-1">
        {stars.map(star => (
          <Star key={star} className="h-6 w-6 fill-current text-gray-300 lg:h-7 lg:w-7" />
        ))}
        <motion.div
          style={topStarStyles}
          variants={containerVariants}
          animate={isInView ? 'glow' : 'initial'}
          className="text-warning-400 absolute left-0 top-0 flex w-[var(--star-width)] items-center gap-1 overflow-hidden"
        >
          {stars.map(star => (
            <AnimatedStar
              key={star}
              variants={itemVariants}
              className="text-warning-400 h-6 w-6 flex-shrink-0 fill-current lg:h-7 lg:w-7"
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
};
